import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { RedirectInfo, LicenseRedirectPopupData } from './license-redirect.types';
import { LicenseRedirectPopupComponent } from '../license-redirect-popup/license-redirect-popup.component';
@Injectable({
    providedIn: 'root',
})
export class LicenseRedirectService {
    constructor(
        private readonly licenseInfoService: LicenseInfoService,
        private readonly dialog: MatDialog
    ) {}

    /**
     * Simple redirect check using existing license info
     * @returns Promise<boolean> - true if redirect occurred, false otherwise
     */
    checkAndRedirectBasedOnLicense(): Promise<boolean> {
        return new Promise((resolve) => {
            // Wait for license info to be available and check redirect requirement
            this.licenseInfoService.licenseInfo$
                .pipe(
                    filter((license) => license !== null), // Wait until we have license data
                    switchMap(() => this.licenseInfoService.getRedirectRequirement$()),
                    take(1)
                )
                .subscribe((redirectInfo) => {
                    if (redirectInfo.shouldRedirect) {
                        this.showRedirectPopup(redirectInfo);
                        resolve(true); // Redirect will happen (via popup)
                    } else {
                        resolve(false); // No redirect needed
                    }
                });
        });
    }

    private performRedirect(): void {
        const currentUrl = globalThis.location.href;
        const newUrl = this.buildRedirectUrl(currentUrl);

        if (newUrl !== currentUrl) {
            globalThis.location.href = newUrl;
        }
    }

    private showRedirectPopup(redirectInfo: RedirectInfo): MatDialogRef<LicenseRedirectPopupComponent> {
        const dialogData: LicenseRedirectPopupData = { redirectInfo };
        return LicenseRedirectPopupComponent.open(this.dialog, dialogData);
    }

    /**
     * Public method to perform redirect from popup confirmation
     * redirects to MyKempower
     */
    performRedirectFromPopup(): void {
        this.performRedirect();
    }

    private buildRedirectUrl(currentUrl: string): string {
        try {
            const url = new URL(currentUrl);

            //Skip redirect on localhost
            if (url.hostname === 'localhost') {
                return currentUrl;
            }

            const targetDomain = this.buildTargetDomain(url.hostname);

            return `${url.protocol}//${targetDomain}`;
        } catch (error) {
            // If URL parsing fails, return the current URL to prevent redirect
            console.warn('Failed to parse URL for redirect:', currentUrl, error);
            return currentUrl;
        }
    }

    private buildTargetDomain(hostname: string): string {
        // Parse environment from hostname prefix
        let envPrefix = '';
        if (hostname.startsWith('dev.')) envPrefix = 'dev.';
        else if (hostname.startsWith('test.')) envPrefix = 'test.';

        // Always redirect to MyKempower
        return `${envPrefix}mykempower.com`;
    }
}
